import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { TempProduct } from '../components/product-card/product-card.component';
import { StateCleanupService } from './state-cleanup.service';

// export interface Product {
//   id: string; // UUID
//   stripePriceId?: string;
//   name: string;
//   description?: string;
//   monthlyCostGross?: number; // BigInteger
//   monthlyCostNet?: number; // BigInteger
//   currency?: string; // Default is 'EUR'
//   vat?: number; // Default is 19
//   createdAt: Date;
//   updatedAt: Date;
// }

interface SubscriptionPlan {
  id: string;
  name: string;
  monthly_cost_gross: number;
  /** Anzeigename eines Vertragsplans (Migration 081). */
  display_name?: string | null;
  /** Individueller Vertrag: Abrechnung per Rechnung, kein Stripe-Kauf,
   *  Planwechsel oder Kuendigung im Portal. */
  is_contract?: boolean;
}

export interface ClientSubscription {
  client_id: string;
  current_plan: SubscriptionPlan;
  /** 'month' | 'year' of the active Stripe subscription (backend migration 029).
   * Empty string for subscriptions sold before annual prices existed — treat
   * that as monthly. Without it the pricing page cannot tell a monthly plan
   * from a yearly one and marks both as active. */
  billing_interval?: 'month' | 'year' | '';
  subscription_start_date: string;
  subscription_end_date: string;
  scheduled_downgrade_date: string;
  scheduled_downgrade_plan: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  products$ = new BehaviorSubject<TempProduct[]>([
    // Trial plans

    {
      id: 'df963d81-8ec3-4d7a-9035-f204d494eb73',
      price: 0,
      description: '',
      isSelected: false,
      hasTrial: false,
      isVisible: false,
      items: '',
      name: 'products.trial.name',
      retentionDays: 730,
    },

    //Normal plans
    {
      id: 'ed738dc0-483a-4d1e-a779-b7fb29f8c5b0',
      name: 'products.free.name',
      price: 0,
      description: 'products.free.description',
      isSelected: true,
      items: 'products.free.features',
      retentionDays: 30,
    },
    {
      id: '8a82df0e-0f0e-4f7b-9dca-d9030ad73cec',
      stripePriceId: environment.production
        ? 'price_1QLpV2FDIdHNufMyaEtWMire'
        : 'price_1QP5MNFDIdHNufMyjHJFmiSm',
      name: 'products.basic.name',
      price: 9900,
      annualPrice: 94800, // 948 EUR NET/yr (Stripe price_1U667j…, tax_behavior=exclusive).
      // Net like the monthly price — both intervals must share one tax treatment
      // or Stripe rejects a portal interval switch. Backend picks the id by interval.
      description: 'products.basic.description',
      isSelected: false,
      items: 'products.basic.features',
      retentionDays: 180,
    },
    {
      id: 'da27b03f-4a42-4d47-aa7f-2978b1053bd2',
      stripePriceId: environment.production
        ? 'price_1QLpW0FDIdHNufMySxZ30gyA'
        : 'price_1QP5LuFDIdHNufMy3Bv4y5r6',
      name: 'products.professional.name',
      price: 24900,
      annualPrice: 238800, // 2388 EUR NET/yr (Stripe price_1U66Bt…, tax_behavior=exclusive).
      description: 'products.professional.description',
      isSelected: false,
      hasTrial: true,
      items: 'products.professional.features',
      retentionDays: 730,
    },
    {
      id: '4',
      name: 'products.enterprise.name',
      price: -1,
      description: 'products.enterprise.description',
      isSelected: false,
      items: 'products.enterprise.features',
    },
  ]);

  activeProduct$ = new BehaviorSubject<TempProduct | undefined>(undefined);

  currentPlan$ = new BehaviorSubject<ClientSubscription | undefined>(undefined);

  constructor(
    protected http: HttpClient,
    protected stateCleanupService: StateCleanupService
  ) {
    this.stateCleanupService.register(() => this.resetState());
  }

  // listProducts() {
  //   return this.http
  //     .get<{ data: Product[] }>(`${environment.userBackend}/plans`)
  //     .pipe(
  //       map((data) => data.data),
  //       tap((products) => {
  //         // this.products$.next(products);
  //       })
  //     );
  // }

  getActivePlan() {
    return this.http
      .get<{ data: ClientSubscription }>(
        `${environment.userBackend}/clients/plan`
      )
      .pipe(
        map((data) => data.data),
        tap((data) => {
          this.currentPlan$.next(data);

          this.activeProduct$.next(
            this.products$.value.find((p) => p.id === data.current_plan.id)
          );
        })
      );
  }

  cancelSubscription() {
    return this.http
      .post(
        `${environment.userBackend}/clients/subscription/cancel${
          environment.production ? '' : '?test_mode=true'
        }`,
        {}
      )
      .pipe(
        tap(() => {
          console.log('Subscription cancelled');
        })
      );
  }

  getPlanById(id: string | undefined) {
    return this.products$.value.find((p) => p.id === id);
  }

  /** Aktueller Plan ist ein individueller Vertrag (GET /clients/plan). */
  get isContract(): boolean {
    return !!this.currentPlan$.value?.current_plan?.is_contract;
  }

  private resetState() {
    this.activeProduct$.next(undefined);
    this.currentPlan$.next(undefined);
  }
}
